// Formulario de ofertas (NUEVA VERSIÓN SIDEBAR)

import React, { useState } from 'react';
import { Send, Check, ChevronDown, Clock, Info } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { formatNumber } from '../../utils/formatCurrency';

/**
 * Subcomponente: Dropdown colapsable de condiciones positivas
 * Solo muestra condiciones tipo "positive"
 */
const PositiveConditionsDropdown = ({ availableConditions, selectedConditions, onToggle }) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);

  // Filtrar solo condiciones positivas
  const positiveConditions = availableConditions.filter(c => c.type === 'positive');

  // Si no hay condiciones positivas, no mostrar nada
  if (positiveConditions.length === 0) return null;

  return (
    <div className="border border-slate-700 rounded-lg overflow-hidden">
      {/* Header colapsable */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between bg-slate-900/30 px-3 py-2 hover:bg-slate-900/50 transition-colors"
      >
        <span className="text-xs font-medium text-slate-300">
          {t('bidForm.strengths')}{selectedConditions.length > 0 && ` (${selectedConditions.length})`}
        </span>
        <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {/* Contenido colapsable */}
      {isOpen && (
        <div className="p-2 space-y-1 bg-slate-900/10">
          {positiveConditions.map((condition) => {
            const isSelected = selectedConditions.includes(condition.id);

            return (
              <button
                key={condition.id}
                type="button"
                onClick={() => onToggle(condition.id)}
                className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-lg transition-all text-left ${
                  isSelected
                    ? 'bg-emerald-500/10 border border-emerald-500/30'
                    : 'hover:bg-slate-800/30'
                }`}
              >
                <div className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 ${
                  isSelected
                    ? 'bg-emerald-500 border-emerald-500'
                    : 'border-slate-600'
                }`}>
                  {isSelected && <Check className="w-3 h-3 text-slate-900" />}
                </div>
                <span className="text-base">{condition.icon}</span>
                <span className={isSelected ? 'text-emerald-200' : 'text-slate-300'}>
                  {condition.label}
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

/**
 * Componente principal: BidFormCard
 * Formulario de ofertas adaptado para sidebar vertical
 *
 * ✅ MODIFICADO: Ahora permite a TODOS los usuarios invitados pujar en última oportunidad
 *
 * @param {boolean} isExpired - Si la propiedad está cerrada
 * @param {boolean} isLastChance - Si está en periodo de última oportunidad
 * @param {object} userLastBid - Última oferta del usuario (puede ser null)
 * @param {boolean} isTopBidder - Si el usuario es el ganador actual
 * @param {boolean} canBidInLastChance - Si puede pujar en última oportunidad
 * @param {string} bidAmount - Valor del input de cantidad
 * @param {function} setBidAmount - Setter para cantidad
 * @param {function} setValidationError - Setter para errores de validación
 * @param {string} validationError - Mensaje de error actual
 * @param {function} handleBidSubmit - Handler del submit
 * @param {array} availableConditions - Condiciones disponibles (solo se usan las positivas)
 * @param {array} selectedConditions - IDs de condiciones seleccionadas
 * @param {function} setSelectedConditions - Setter para condiciones
 * @param {string} conditionsText - Texto libre de condiciones/comentarios
 * @param {function} setConditionsText - Setter para texto libre
 */
export const BidFormCard = ({
  isExpired,
  isLastChance,
  userLastBid,
  isTopBidder,
  canBidInLastChance,
  bidAmount,
  setBidAmount,
  setValidationError,
  validationError,
  handleBidSubmit,
  availableConditions = [],
  selectedConditions = [],
  setSelectedConditions,
  conditionsText = '',
  setConditionsText,
  lastChanceBidRange,
  maxBid,
  userBlindOffer,
  propertyStatus
}) => {
  const { t, i18n } = useTranslation();
  const [showComments, setShowComments] = useState(false);

  // Calcular el mínimo del input: en fase OPEN, usar oferta ciega si existe
  const inputMinValue = (propertyStatus === 'OPEN' && userBlindOffer?.amount)
    ? userBlindOffer.amount
    : 1;

  // Handler para toggle de condiciones
  const handleConditionToggle = (conditionId) => {
    setSelectedConditions(prev =>
      prev.includes(conditionId)
        ? prev.filter(id => id !== conditionId)
        : [...prev, conditionId]
    );
  };

  // Determinar placeholder dinámico
  const hasUserBid = !!userLastBid;
  const placeholderAmount = hasUserBid ? "Tu oferta" : formatNumber(maxBid || 0, i18n.language);

  // ✅ MODIFICADO: Validación de última oportunidad (ahora solo bloquea si es top bidder o ya usó su oportunidad)
  if (isLastChance) {
    const canBid = !isTopBidder && canBidInLastChance;

    if (!canBid) {
      return (
        <div className="bg-amber-500/10 backdrop-blur-xl rounded-xl p-4 border border-amber-500/20 text-center">
          <Clock className="w-8 h-8 text-amber-400 mx-auto mb-2" />
          <h3 className="text-sm font-bold text-white mb-1">
            {isTopBidder
              ? t('bidForm.lastChance.youAreWinner')
              : t('bidForm.lastChance.alreadyUsed')
            }
          </h3>
          <p className="text-xs text-slate-400">
            {isTopBidder
              ? t('bidForm.lastChance.makeReservation')
              : t('bidForm.lastChance.onlyOnce')
            }
          </p>
        </div>
      );
    }
  }

  // Si está cerrada
  if (isExpired) {
    return (
      <div className="bg-slate-800/50 backdrop-blur-xl rounded-xl p-4 border border-slate-700/50 text-center">
        <Clock className="w-8 h-8 text-slate-400 mx-auto mb-2" />
        <h3 className="text-sm font-bold text-white mb-1">{t('bidForm.closed.title')}</h3>
        <p className="text-xs text-slate-400">
          {t('bidForm.closed.desc')}
        </p>
      </div>
    );
  }

  return (
    <div className={`backdrop-blur-xl rounded-xl p-4 border ${
      isLastChance
        ? 'bg-gradient-to-br from-amber-500/10 to-orange-500/10 border-amber-500/30'
        : 'bg-slate-800/50 border-emerald-500/30'
    }`}>
      <h3 className={`text-base font-bold mb-3 flex items-center gap-2 ${
        isLastChance ? 'text-amber-400' : 'text-white'
      }`}>
        {isLastChance && <Clock className="w-4 h-4" />}
        {isLastChance ? t('bidForm.titleLastChance') : t('bidForm.titleNormal')}
      </h3>

      {isLastChance && lastChanceBidRange && (
        <p className="text-xs text-amber-200/80 mb-3">
          {t('bidForm.validRange', { min: formatNumber(lastChanceBidRange.min, i18n.language), max: formatNumber(lastChanceBidRange.max, i18n.language) })}
        </p>
      )}

      <form onSubmit={handleBidSubmit} className="space-y-3">
        {/* Input de cantidad */}
        <div>
          <div className="relative">
            <input
              type="number"
              required
              min={inputMinValue}
              value={bidAmount}
              onChange={(e) => {
                setBidAmount(e.target.value);
                setValidationError('');
              }}
              placeholder={placeholderAmount}
              className="w-full bg-slate-900/50 border border-slate-700 rounded-lg pl-3 pr-8 py-2.5 text-white text-lg font-semibold focus:outline-none focus:border-amber-500 placeholder-slate-600 [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
            />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 font-semibold text-sm">€</span>
          </div>
        </div>

        {/* Error de validación */}
        {validationError && (
          <div className="text-red-400 text-xs bg-red-500/10 border border-red-500/20 rounded-lg p-2">
            {validationError}
          </div>
        )}

        {/* Condiciones positivas (colapsable) */}
        {availableConditions.length > 0 && (
          <PositiveConditionsDropdown
            availableConditions={availableConditions}
            selectedConditions={selectedConditions}
            onToggle={handleConditionToggle}
          />
        )}

        {/* Comentarios opcionales (colapsable) */}
        <div>
          <button
            type="button"
            onClick={() => setShowComments(!showComments)}
            className="flex items-center gap-2 text-sm text-slate-400 hover:text-slate-300"
          >
            <Info className="w-4 h-4" />
            {showComments ? t('bidForm.hideComments') : t('bidForm.addComments')}
          </button>

          {showComments && (
            <div className="mt-2">
              <textarea
                value={conditionsText}
                onChange={(e) => setConditionsText(e.target.value.slice(0, 500))}
                maxLength={500}
                rows={3}
                placeholder={t('bidForm.commentsPlaceholder')}
                className="w-full bg-slate-900/50 border border-slate-600 rounded-lg px-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:border-amber-500 text-sm resize-none"
              />
              <div className="text-right text-xs text-slate-500 mt-1">
                {conditionsText.length}/500
              </div>
            </div>
          )}
        </div>

        {/* Botón de envío */}
        <button
          type="submit"
          className="w-full bg-gradient-to-r from-emerald-500 to-emerald-600 text-white font-semibold py-3 rounded-lg hover:from-emerald-600 hover:to-emerald-700 transition-all flex items-center justify-center gap-2"
        >
          <Send className="w-5 h-5" />
          {isLastChance ? t('bidForm.submitLastChance') : t('bidForm.submitNormal')}
        </button>
      </form>
    </div>
  );
};

export default BidFormCard;
